import { useState, useMemo } from "react";
import { Plus, Eye, X, Edit, Trash2, RefreshCcw } from "lucide-react";
import { getPriorityColor } from "../../utils/colors";
import MeetingAdd from "./MeetingAdd";
import { FiUsers } from "react-icons/fi";

const formatText = (text = "") =>
    text
        .toLowerCase()
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const MeetingTab = ({
  getStatusColor,
  onRefreshAccounts,
  heading,
  allTasks,
  type,
  accountId,
  workflowDropdown,
  formData, 
  setFormData,
  handleSubmit,
  isLoader,
  showCreateTask, 
  setShowCreateTask,
  handleDeleteTask,
  taskId,
  setTaskId,
  clients,
  workflowTemplates
}) => {
  const [viewTask, setViewTask] = useState(null);
  
  const [filters, setFilters] = useState({
    meetingType: "all"
  });

  const filteredTasks = useMemo(() => {
    if (!allTasks) return [];

    let tasks = [...allTasks];

    // Filter
    if (filters.meetingType !== "all") {
      tasks = tasks.filter(
        (task) => task?.meetingType === filters.meetingType
      );
    }

    return tasks;
  }, [allTasks, filters]);

  const meetingType = [
    ...new Set(
      allTasks
        ?.map((t) => t?.meetingType)
        .filter(Boolean)
    ),
  ];

  const formatDate = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString();
    };
  const formatTime = (dateStr) => {
    if (!dateStr) return "--:--";
    const date = new Date(dateStr);
    return isNaN(date.getTime())
      ? ""
      : date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDateTime = (date) =>
  date ? new Date(date).toLocaleString("en-IN") : "N/A";

  const [sortConfig, setSortConfig] = useState({
      key: null,
      direction: "asc", // asc | desc
    });
  
    const handleSort = (key) => {
      setSortConfig((prev) => {
        if (prev.key === key) {
          return {
            key,
            direction: prev.direction === "asc" ? "desc" : "asc",
          };
        }
        return { key, direction: "asc" };
      });
    };
  
    const getValueByPath = (obj, path) =>
        path.split(".").reduce((acc, key) => acc?.[key], obj);
  
  
      const sortedTasks = useMemo(() => {
        if (!sortConfig.key) return filteredTasks;
  
        return [...filteredTasks].sort((a, b) => {
          const aVal = getValueByPath(a, sortConfig.key);
          const bVal = getValueByPath(b, sortConfig.key);
  
          if (!aVal) return 1;
          if (!bVal) return -1;
  
          if (typeof aVal === "string") {
            return sortConfig.direction === "asc"
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          }
  
          return sortConfig.direction === "asc"
            ? aVal > bVal ? 1 : -1
            : aVal < bVal ? 1 : -1;
        });
      }, [filteredTasks, sortConfig]);
  
  
    const SortIcon = ({ column }) => {
      const isActive = sortConfig.key === column;
  
      return (
        <span className="inline-flex flex-col ml-1 text-[9px] leading-none">
          <span
            className={
              isActive && sortConfig.direction === "asc"
                ? "text-gray-900"
                : "text-gray-300"
            }
          >
            ▲
          </span>
          <span
            className={
              isActive && sortConfig.direction === "desc"
                ? "text-gray-900"
                : "text-gray-300"
            }
          >
            ▼
          </span>
        </span>
      );
    };


  return (
    <div className="space-y-3">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-2 py-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-lg font-bold text-slate-700">{heading}</h3>
          <button
            onClick={() => setShowCreateTask(true)}
            className="bg-primary text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 w-full sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            <span>Add Meeting</span>
          </button>
        </div>

        {allTasks?.length !== 0 ?
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
          <select
            className="border rounded-lg px-3 py-1 text-sm"
            value={filters.meetingType}
            onChange={(e) => setFilters({ ...filters, meetingType: e.target.value })}
          >
            <option value="all">All Status</option>
            {meetingType.map((s, idx) => (
              <option key={idx} value={s}>
                {formatText(s)}
              </option>
            ))}
          </select>
          <div className="flex items-end justify-end">
            <button
                type="button"
                onClick={() => setFilters({
                  meetingType: "all"
                })}
                className="bg-primary py-1 px-2 rounded-md text-sm font-medium text-white hover:bg-primary [&:active_span]:animate-spin-once"
            >
                <span className="inline-block">
                                   <RefreshCcw size={18} />
                                 </span>
            </button>
          </div>
        </div>:<></>}
        <div className="py-2 mt-1">
          {filteredTasks?.length !== 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left text-slate-700">#</th>
                    <th className="px-2 py-1 text-left text-slate-700 cursor-pointer" onClick={() => handleSort("meetingType")}><span className="flex flex-row items-center gap-1">Meeting Type <SortIcon column="meetingType" /></span></th>
                    <th className="px-2 py-1 text-left text-slate-700 cursor-pointer" onClick={() => handleSort("presentMembers")}><span className="flex flex-row items-center gap-1">Participants <SortIcon column="presentMembers" /></span></th>
                    <th className="px-2 py-1 text-left text-slate-700 cursor-pointer" onClick={() => handleSort("meetingDate")}><span className="flex flex-row items-center gap-1">Date <SortIcon column="meetingDate" /></span></th>
                    <th className="px-2 py-1 text-left text-slate-700 cursor-pointer" onClick={() => handleSort("meetingDate")}><span className="flex flex-row items-center gap-1">Time <SortIcon column="meetingDate" /></span></th>
                    <th className="px-2 py-1 text-left text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedTasks.map((task, index) => (
                    <tr key={task?.id} className="hover:bg-gray-50">
                      <td className="px-2 py-1">{index + 1}</td>
                      <td className="px-2 py-1 font-medium">{formatText(task.meetingType)}</td>
                      <td className="px-2 py-1">{task?.presentMembers ?? "N/A"}</td>
                      <td className="px-2 py-1">{formatDate(task?.meetingDate)}</td>
                      <td className="px-2 py-1">{formatTime(task?.meetingDate)}</td>
                      <td className="px-2 py-1 text-center">
                        <div className="flex flex-row items-center justify-center gap-1">
                          <button
                            onClick={() => setViewTask(task)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Eye size={14} />
                          </button>
                          
                          <button
                            onClick={() => {setTaskId(task?.id); setFormData({
                                id: task.id || "",
                                meetingType: task.meetingType || "",
                                meetingDate: task.meetingDate || "",
                                presentMembers: task.presentMembers || "",
                                noticeIssueDate: task.noticeIssueDate || "",
                                agenda: task.agenda || "",
                              });}}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit size={14} />
                          </button>
                          
                          <button
                            onClick={() => handleDeleteTask(task?.id)}
                            className="p-1 text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
              <FiUsers className="h-12 w-12 text-gray-400 mx-auto mb-1" />
              <h4 className="text-lg font-medium text-gray-900 mb-1">No Meeting</h4>
              <p className="text-gray-600 mb-4">
                No Meeting found. Create your first Meeting to get started.
              </p>
            </div>
          )}
        </div>
      </div>


      {showCreateTask && 
      <div onClick={() => setShowCreateTask(false)} className="fixed -top-6 inset-0 bg-black/40 flex items-center justify-center">
        <MeetingAdd
          type={type}
          clients={clients}
          workflowDropdown={workflowDropdown}
          onRefreshAccounts={onRefreshAccounts}
          onClose={() => setShowCreateTask(false)}
          setShowCreateTask={setShowCreateTask}
          workflowTemplates={workflowTemplates}
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          isLoader={isLoader}
        />
      </div>}

      {taskId && 
      <div onClick={() => setTaskId(null)} className="fixed -top-6 inset-0 bg-black/40 flex items-center justify-center">
        <MeetingAdd
          type={type}
          clients={clients}
          workflowDropdown={workflowDropdown}
          onRefreshAccounts={onRefreshAccounts}
          onClose={() => setTaskId(null)}
          setShowCreateTask={setTaskId}
          workflowTemplates={workflowTemplates}
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          isLoader={isLoader}
          workflowId={taskId}
          accountId={accountId}
        />
      </div>}

      {viewTask && (
      <div
        onClick={() => setViewTask(null)}
        className="fixed inset-0 -top-6 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[94vh] overflow-x-auto"
        >
          {/* Header */}
          <div className="flex justify-between items-center px-2 py-2 border-b">
            <div className="text-lg font-semibold text-gray-900">
              Meetings Details
            </div>
            <button
              onClick={() => setViewTask(null)}
              className="p-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="p-2 text-sm space-y-1">
            {/* Title */}
            <div>
              <div className="text-lg font-semibold text-gray-900">
                {viewTask?.name}
              </div>
              <div className="text-xs text-gray-400">Task Overview</div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                <InfoCard
                    label="Meeting Date"
                    value={formatDateTime(viewTask?.meetingDate)}
                />

                <InfoCard
                    label="Notice Issue Date"
                    value={formatDateTime(viewTask?.noticeIssueDate)}
                />

                <InfoCard
                    label="Meeting Type"
                    value={formatText(viewTask?.meetingType)}
                />

                <InfoCard
                    label="Present Members"
                    value={viewTask?.presentMembers || "N/A"}
                />
                </div>


            {/* Description */}
            {viewTask?.agenda && (
                <div className="border-t pt-2 mt-2">
                    <p className="text-xs text-gray-400 mb-1">Agenda</p>
                    <div className="bg-gray-50 rounded-lg px-3 py-2 text-gray-700 text-sm">
                    {viewTask.agenda}
                    </div>
                </div>
                )}

          </div>
        </div>
      </div>)}
    </div>
  );
};

export default MeetingTab;


const InfoCard = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg px-2 py-2">
    <div className="text-xs text-gray-400">{label}</div>
    <div className="font-medium text-gray-800">{value}</div>
  </div>
);

const StatusBadge = ({ status }) => (
  <div className="bg-gray-50 rounded-lg p-2">
    <div className="text-xs text-gray-400">Status</div>
    <span
      className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold mt-1
        ${
          status === "TODO"
            ? "bg-gray-200 text-gray-700"
            : status === "IN_PROGRESS"
            ? "bg-blue-100 text-blue-600"
            : "bg-green-100 text-green-600"
        }
      `}
    >
      {formatText(status)}
    </span>
  </div>
);

